import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Edit2, Trash2, Eye } from 'lucide-react';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Modal from '../components/Modal';
import { FF, FS, ErrMsg } from '../components/FormField';
import { FilterBar, SearchInput, FilterSelect, PageHeader } from '../components/FilterBar';
import CompanyDeptFilter from '../components/CompanyDeptFilter';
import SearchableSelect from '../components/SearchableSelect';

const LICENSE_TYPES = ['windows','office','antivirus','adobe','other'];
const STATUSES = ['available','in_use','expired','blacklisted'];

const EXPIRY_PRESETS = [
  { value: '', label: 'No expiry date' },
  { value: '1_year', label: '1 Year from today' },
  { value: '2_years', label: '2 Years from today' },
  { value: '3_years', label: '3 Years from today' },
  { value: '5_years', label: '5 Years from today' },
  { value: 'lifetime', label: 'Lifetime (no expiry)' },
  { value: 'custom', label: 'Custom date' },
];

function calcExpiryDate(preset, purchasedDate) {
  const base = purchasedDate ? new Date(purchasedDate) : new Date();
  const map = { '1_year': 1, '2_years': 2, '3_years': 3, '5_years': 5 };
  if (map[preset]) { const d = new Date(base); d.setFullYear(d.getFullYear() + map[preset]); return d.toISOString().split('T')[0]; }
  return null;
}

export default function Licenses() {
  const { isIT, isAdmin, isDirector, isFinance } = useAuth();
  const showCompanyFilter = isAdmin || isDirector || isFinance;
  const [rows, setRows] = useState([]); const [total, setTotal] = useState(0); const [pages, setPages] = useState(1); const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(''); const [filterType, setFilterType] = useState(''); const [filterStatus, setFilterStatus] = useState('');
  const [filterCompany, setFilterCompany] = useState([]); const [filterDept, setFilterDept] = useState(null);
  const [modal, setModal] = useState(null); const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({}); const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const [assets, setAssets] = useState([]);
  const [expiryPreset, setExpiryPreset] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search; if (filterType) params.license_type = filterType; if (filterStatus) params.status = filterStatus;
      if (filterCompany?.length) params.company_id = Array.isArray(filterCompany) ? filterCompany.join(',') : filterCompany;
      if (filterDept) params.department_id = filterDept;
      const r = await api.get('/licenses', { params });
      setRows(r.data.data || []); setTotal(r.data.total || 0); setPages(r.data.pages || 1);
    } catch {} finally { setLoading(false); }
  }, [page, search, filterType, filterStatus, filterCompany, filterDept]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/assets', { params: { limit: 200, status: 'in_use' } }).then(r => setAssets(r.data.data || [])).catch(() => {});
  }, []);

  const handleExpiryPreset = (preset) => {
    setExpiryPreset(preset);
    if (preset === 'lifetime' || preset === '') {
      setForm(prev => ({ ...prev, expiry_date: '' }));
    } else if (preset !== 'custom') {
      const date = calcExpiryDate(preset, form.purchased_date);
      if (date) setForm(prev => ({ ...prev, expiry_date: date }));
    }
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      selected ? await api.put(`/licenses/${selected.id}`, form) : await api.post('/licenses', form);
      setModal(null); setSelected(null); load();
    } catch (err) { setError(err.response?.data?.error || 'Save failed'); } finally { setSaving(false); }
  };

  const isExpired = d => d && new Date(d) < new Date();

  const columns = [
    { header: 'License Key', render: r => <div><div className="font-mono text-xs font-semibold truncate max-w-xs" style={{color:'#60a5fa'}}>{r.license_key}</div></div> },
    { header: 'Type', render: r => <Badge value={r.license_type}/> },
    { header: 'Status', render: r => <Badge value={r.status}/> },
    { header: 'Linked Asset', render: r => r.linkedAsset ? <span className="font-mono text-xs" style={{color:'#60a5fa'}}>{r.linkedAsset.asset_number}</span> : <span style={{color:'#334155'}}>—</span> },
    { header: 'Vendor', render: r => r.vendor || <span style={{color:'#334155'}}>—</span> },
    { header: 'Expiry', render: r => r.expiry_date
      ? <span style={{color: isExpired(r.expiry_date)?'#f87171':'#94a3b8'}}>{r.expiry_date}{isExpired(r.expiry_date)?' (Expired)':''}</span>
      : <span style={{color:'#334155'}}>—</span>
    },
    { header: 'Price', render: r => r.purchase_price ? `LKR ${Number(r.purchase_price).toLocaleString()}` : <span style={{color:'#334155'}}>—</span> },
    { header: '', render: r => (
      <div className="flex gap-1">
        <button onClick={() => { setSelected(r); setModal('view'); }} className="btn-ghost px-2 py-1 text-xs" title="View" style={{color:'#60a5fa'}}><Eye size={11}/></button>
        {isIT && <button onClick={() => { setSelected(r); setForm({...r}); setExpiryPreset('custom'); setError(''); setModal('form'); }} className="btn-ghost px-2 py-1 text-xs"><Edit2 size={11}/></button>}
        {isAdmin && <button onClick={async () => { if(window.confirm('Delete?')) { await api.delete(`/licenses/${r.id}`); load(); }}} className="btn-ghost px-2 py-1 text-xs" style={{color:'#ef4444'}}><Trash2 size={11}/></button>}
      </div>
    )}
  ];

  return (
    <div className="space-y-4">
      <PageHeader title="License Keys" count={total}>
        <div className="flex items-center gap-2 flex-wrap">
          {showCompanyFilter && <CompanyDeptFilter companyId={filterCompany} deptId={filterDept} onChange={(c,d) => { setFilterCompany(c); setFilterDept(d); setPage(1); }}/>}
          <button onClick={load} className="btn-ghost p-2"><RefreshCw size={13}/></button>
          {isIT && <button onClick={() => { setSelected(null); setForm({license_type:'windows',status:'available'}); setExpiryPreset(''); setError(''); setModal('form'); }} className="btn-primary"><Plus size={13}/> Add License</button>}
        </div>
      </PageHeader>

      <FilterBar hasFilters={!!(search||filterType||filterStatus)} onClear={() => { setSearch(''); setFilterType(''); setFilterStatus(''); setPage(1); }}>
        <SearchInput value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search license key, vendor..."/>
        <FilterSelect value={filterType} onChange={v => { setFilterType(v); setPage(1); }} placeholder="All Types" options={LICENSE_TYPES.map(t=>({value:t,label:t}))}/>
        <FilterSelect value={filterStatus} onChange={v => { setFilterStatus(v); setPage(1); }} placeholder="All Statuses" options={STATUSES.map(s=>({value:s,label:s}))}/>
      </FilterBar>

      <Table columns={columns} data={rows} loading={loading} page={page} pages={pages} total={total} onPage={setPage}/>

      {modal === 'form' && (
        <Modal title={selected ? 'Edit License' : 'Add License'} onClose={() => { setModal(null); setSelected(null); }}>
          <form onSubmit={handleSave} className="space-y-4">
            <ErrMsg msg={error}/>
            <div className="grid grid-cols-2 gap-3">
              {/* License Key */}
              <div className="col-span-2"><FF label="License Key *" name="license_key" form={form} setForm={setForm} required placeholder="XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"/></div>

              {/* Type */}
              <FS label="Type" name="license_type" form={form} setForm={setForm} options={LICENSE_TYPES.map(t=>({val:t,label:t}))}/>

              {/* Linked Asset — MANDATORY */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Linked Asset *</label>
                <SearchableSelect required value={form.linked_asset_id||''} onChange={v=>setForm({...form,linked_asset_id:v||null})} placeholder="— Select Asset —"
                  options={assets.map(a=>({value:a.id,label:`${a.asset_number} — ${a.brand} ${a.model}`}))}/>
              </div>

              <FF label="Vendor / Supplier" name="vendor" form={form} setForm={setForm}/>
              <FF label="Purchase Price (LKR)" name="purchase_price" type="number" form={form} setForm={setForm}/>
              <FF label="Purchase Date" name="purchased_date" type="date" form={form} setForm={setForm}/>

              {/* Expiry — dropdown preset or custom */}
              <div>
                <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Expiry Period</label>
                <select value={expiryPreset} onChange={e=>handleExpiryPreset(e.target.value)} className="input-dark">
                  {EXPIRY_PRESETS.map(p=><option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {expiryPreset === 'lifetime' ? (
                <div className="flex items-center px-3 py-2 rounded-xl text-xs" style={{background:'rgba(129,140,248,0.1)',border:'1px solid rgba(129,140,248,0.2)',color:'#818cf8'}}>
                  ∞ Lifetime — no expiry date
                </div>
              ) : (
                <div>
                  <label className="block text-xs font-medium mb-1" style={{color:'#475569'}}>Expiry Date {expiryPreset && expiryPreset !== 'custom' ? '(auto-calculated)' : ''}</label>
                  <input type="date" value={form.expiry_date||''} onChange={e=>setForm({...form,expiry_date:e.target.value})}
                    className="input-dark" readOnly={expiryPreset !== 'custom' && expiryPreset !== ''}/>
                </div>
              )}

              <FS label="Status" name="status" form={form} setForm={setForm} options={STATUSES.map(s=>({val:s,label:s}))}/>
            </div>
            <FF label="Remarks" name="remarks" form={form} setForm={setForm} rows={2}/>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setModal(null); setSelected(null); }} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center">{saving?'Saving...':'Save'}</button>
            </div>
          </form>
        </Modal>
      )}
      {modal === 'view' && selected && (
        <Modal title="License Details" onClose={() => { setModal(null); setSelected(null); }}>
          <div className="space-y-3">
            {[
              ['License Key', <span className="font-mono text-sm" style={{color:'#60a5fa',wordBreak:'break-all'}}>{selected.license_key}</span>],
              ['Type', <Badge value={selected.license_type}/>],
              ['Status', <Badge value={selected.status}/>],
              ['Product Name', selected.product_name||'—'],
              ['Vendor', selected.vendor||'—'],
              ['Seats', selected.seats||'—'],
              ['Purchase Price', selected.purchase_price ? `LKR ${Number(selected.purchase_price).toLocaleString()}` : '—'],
              ['Purchase Date', selected.purchased_date||'—'],
              ['Expiry Date', selected.expiry_date ? <span style={{color:isExpired(selected.expiry_date)?'#f87171':'#34d399'}}>{selected.expiry_date}{isExpired(selected.expiry_date)?' — EXPIRED':''}</span> : 'No expiry'],
              ['Linked Asset', selected.linkedAsset?.asset_number||'—'],
              ['Remarks', selected.remarks||'—'],
            ].map(([k,v]) => (
              <div key={k} className="flex items-start gap-3 py-2" style={{borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                <span className="text-xs flex-shrink-0 w-32" style={{color:'#475569'}}>{k}</span>
                <span className="text-xs flex-1" style={{color:'#e2e8f0'}}>{v}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}
